const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getBaseUrl = () => {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured. Please set REACT_APP_API_BASE_URL.');
  }
  return API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
};

const handleResponse = async (response) => {
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }
  // Check for both 'success' and 'status' fields (different APIs use different formats)
  if (json && (json.success === false || json.status === false)) {
    throw new Error(json?.message || 'API responded with an error.');
  }
  return json;
};

export const rewardService = {
  async createReward(payload) {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/rewards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async getRewards() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/rewards`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    return Array.isArray(json?.data) ? json.data : [];
  },

  async getActiveRewards() {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/rewards/activeRewards`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    // Handle response structure: { status: true, message: "...", data: [...], extra: {...} }
    if (json?.status === true && Array.isArray(json?.data)) {
      return json.data;
    }
    // Fallback for other response formats
    return Array.isArray(json?.data) ? json.data : [];
  },

  async updateReward(rewardId, payload) {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/rewards/${rewardId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async deleteReward(rewardId) {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/rewards/${rewardId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },

  async redeemReward(rewardId, associateId) {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/rewards/redeemRequest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rewardId: rewardId,
        associateId: associateId,
      }),
    });
    return handleResponse(response);
  },

  async getRedeemRequests(requestStatus = 'all', page = 1) {
    const base = getBaseUrl();
    const params = new URLSearchParams();
    if (requestStatus && requestStatus !== 'all') {
      params.append('requestStatus', requestStatus);
    }
    if (page) {
      params.append('page', page);
    }
    
    const queryString = params.toString();
    const url = queryString 
      ? `${base}/api/rewards/redeemRequestList?${queryString}`
      : `${base}/api/rewards/redeemRequestList`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    return {
      data: Array.isArray(json?.data) ? json.data : [],
      extra: json?.extra || {},
    };
  },

  async approveRedeemRequest(requestId) {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/rewards/approveRewardRequest`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        _id: requestId,
      }),
    });
    return handleResponse(response);
  },

  async rejectRedeemRequest(requestId) {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/rewards/rejectRewardRequest`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        _id: requestId,
      }),
    });
    return handleResponse(response);
  },

  async getRewardTransactions(associateId = null, transactionStatus = 'all') {
    const base = getBaseUrl();
    const params = new URLSearchParams();
    if (associateId && associateId !== 'all') {
      params.append('_id', associateId);
    }
    if (transactionStatus && transactionStatus !== 'all') {
      params.append('transactionStatus', transactionStatus);
    }
    
    const queryString = params.toString();
    const url = queryString 
      ? `${base}/api/rewards/rewardTransactionList?${queryString}`
      : `${base}/api/rewards/rewardTransactionList`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await handleResponse(response);
    return Array.isArray(json?.data) ? json.data : [];
  },

  async getMyRewardAchievements(params = {}) {
    const base = getBaseUrl();
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const url = `${base}/api/rewards/myRewardAchievements?${queryParams.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
    });
    return handleResponse(response);
  },

  async getMyIncentiveAchievements(params = {}) {
    const base = getBaseUrl();
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${base}/api/IncentiveGenerator/myIncentiveAchievements?${queryParams.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
    });
    return handleResponse(response);
  },
};

